import {
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Tooltip,
    Typography,
    Drawer,
    FormControl,
    FormGroup,
    FormControlLabel,
    Checkbox,
    InputLabel,
    MenuItem,
    Select,
    Divider,
    Collapse,
    ToggleButtonGroup,
    ToggleButton,
    Grid
} from '@mui/material';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import HelpOverlay from '../common/HelpOverlay';
// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import FolderIcon from '@mui/icons-material/Folder';
import GridViewIcon from '@mui/icons-material/GridView';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ViewListIcon from '@mui/icons-material/ViewList';
import FilterListIcon from '@mui/icons-material/FilterList';
import TuneIcon from '@mui/icons-material/Tune';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderBreadcrumbs from './ModernBreadcrumbs';
import { userViewHelpItems } from '../../utils/helpItems';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {alpha} from '@mui/material/styles';
// Components
import FileSearchService from '../../services/FileSearchService';
import GridView from './GridView';
import ListView from './ListView';
import AssignmentService from '../../services/AssignmentService';
import { useTheme } from '@mui/material/styles';
// Import SortSelector component
import SortSelector, { SORT_OPTIONS } from '../common/SortSelector';
import { PageHeader } from '../common/PageHeader';
import { getProcessStageColor, getProcessStageLabel } from '../../utils/processStageUtils';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import EmptyState from '../common/EmptyState';
import FileGridItem from './FileGridItem';
import { useAssignmentContext } from '../../contexts/AssignmentContext';

const ModernFolderView = ({
    folders,
    files,
    currentPath,
    onFolderClick,
    onFileClick,
    onCreateFolder,
    onRename,
    onDelete,
    onCreateVideo,
    videoExperience,
    onMenuOpen,
    onRefresh,
    onLogout,
    // Add themeVariant prop with default value
    themeVariant = 'minimalist',
    darkMode,
    toggleDarkMode
}) => {
    // Add state for assignment dialog
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [selectedFileForAssign, setSelectedFileForAssign] = useState(null);

    // Other existing states...
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [sortBy, setSortBy] = useState(() => {
        const savedSort = localStorage.getItem('folderSortBy');
        return savedSort || SORT_OPTIONS.NAME_ASC;
    });
    const [viewMode, setViewMode] = useState('normal'); // 'normal', 'assigned', 'recent'
    const [assignedVideos, setAssignedVideos] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchCurrentFolderOnly, setSearchCurrentFolderOnly] = useState(true);
    const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
    const [openAssignments, setOpenAssignments] = useState([]);
    const [searchFilters, setSearchFilters] = useState({
        languages: [],
        processStages: []
    });
    const [availableLanguages, setAvailableLanguages] = useState([
        'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Chinese', 'Japanese', 'Korean'
    ]);
    const [availableProcessStages, setAvailableProcessStages] = useState([
        { value: 'LEVEL0', label: 'AI Generation Complete' },
        { value: 'LEVEL1', label: 'Level 1 Complete' },
        { value: 'LEVEL2', label: 'Level 2 Complete' },
        { value: 'PUBLISHED', label: 'Published' }
    ]);
    const theme = useTheme();

    // Use the assignment context
    const { 
        loadingAssignments,
        fetchAssignedVideos 
    } = useAssignmentContext();

    const normalizedPath = useMemo(() => {
        let path = currentPath || '/';
        if (!path.startsWith('/')) {
            path = '/' + path;
        }
        return path.replace(/\/+/g, '/');
    }, [currentPath]);

    // Memoize the refresh handler
    const handleRefresh = useCallback(() => {
        onRefresh();
    }, [onRefresh]);

    // Memoize the search handler
    const performSearch = useCallback(async (query) => {
        // Don't search if there are no search criteria
        if (!query.trim() && !searchFilters.languages.length && !searchFilters.processStages.length) {
            setSearchResults(null);
            return;
        }

        try {
            setIsSearching(true);

            // Get auth data from localStorage
            const authData = localStorage.getItem('authToken');
            const userData = JSON.parse(localStorage.getItem('authUser') || '{}');

            console.log('Performing search with criteria:', {
                query: query.trim(),
                languages: searchFilters.languages,
                processStages: searchFilters.processStages,
                currentFolder: searchCurrentFolderOnly ? normalizedPath : null
            });

            const results = await FileSearchService.advancedSearch({
                query: query.trim(),
                email: userData.email,
                auth: authData ? authData.replace(/"/g, '') : userData.auth,
                languages: searchFilters.languages,
                processStages: searchFilters.processStages,
                currentFolder: searchCurrentFolderOnly ? normalizedPath : null
            });

            console.log('Search results received:', results);

            // Ensure we have valid results
            setSearchResults({
                folders: Array.isArray(results.folders) ? results.folders : [],
                files: Array.isArray(results.files) ? results.files : [],
            });
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults({
                folders: [],
                files: [],
                error: error.response?.data?.message || 'Unable to perform search. Please try again later.'
            });
        } finally {
            setIsSearching(false);
        }
    }, [searchFilters, searchCurrentFolderOnly, normalizedPath]);

    // Memoize the clear search handler
    const clearSearch = useCallback(() => {
        console.log('Clearing search term');
        setSearchTerm('');
        setSearchResults(null);
    }, []);

    // Memoize the clear filters handler
    const clearFilters = useCallback(() => {
        console.log('Clearing search filters');
        setSearchFilters({
            languages: [],
            processStages: []
        });
    }, []);

    // Memoize the clear all search handler
    const clearAllSearch = useCallback(() => {
        console.log('Clearing all search criteria');
        setSearchTerm('');
        clearFilters();
        setSearchResults(null);
    }, [clearFilters]);

    // Memoize the toggle advanced search handler
    const toggleAdvancedSearch = useCallback(() => {
        setAdvancedSearchOpen(prev => !prev);
    }, []);

    // Memoize the fetch assigned videos handler
    const fetchAndSetAssignedVideos = useCallback(async () => {
        const result = await fetchAssignedVideos();
        if (result && result.videos) {
            setAssignedVideos(result.videos);
        } else {
            console.error('No assigned videos found or error fetching assigned videos');
            setAssignedVideos([]);
        }
        if(result && result.assignmentData) {
            // filter the ones that are not COMPLETED
            const openAssignments = result.assignmentData.filter(a => a.status !== 'COMPLETED');
            setOpenAssignments(openAssignments);
        }
    }, [fetchAssignedVideos]);

    useEffect(() => {
        // Clear search timeout on component unmount or when dependencies change
        let searchTimer = null;

        // Only set up the timer if we have search criteria
        if (searchTerm.trim().length >= 2 || searchFilters.languages.length > 0 || searchFilters.processStages.length > 0) {
            searchTimer = setTimeout(() => {
                performSearch(searchTerm);
            }, 1000);
        } else if (searchTerm === '') {
            // Reset search results when search is cleared
            setSearchResults(null);
        }

        // Cleanup function to clear timer when component unmounts or dependencies change
        return () => {
            if (searchTimer) {
                clearTimeout(searchTimer);
            }
        };
    }, [searchTerm, searchFilters, performSearch]);

    useEffect(() => {
        if (viewMode === 'assigned') {
            fetchAndSetAssignedVideos();
        }
    }, [viewMode]);

    useEffect(() => {
        // Fetch assigned videos on component mount
        fetchAndSetAssignedVideos();
    }, []); // Empty dependency array means this runs once on mount

    

    const handleLanguageFilterChange = (language) => {
        setSearchFilters(prevFilters => {
            const languages = [...prevFilters.languages];
            const index = languages.indexOf(language);

            if (index === -1) {
                languages.push(language);
            } else {
                languages.splice(index, 1);
            }

            return {
                ...prevFilters,
                languages
            };
        });
    };

    const handleProcessStageFilterChange = (stage) => {
        setSearchFilters(prevFilters => {
            const processStages = [...prevFilters.processStages];
            const index = processStages.indexOf(stage);

            if (index === -1) {
                processStages.push(stage);
            } else {
                processStages.splice(index, 1);
            }

            return {
                ...prevFilters,
                processStages
            };
        });
    };

    const handleSortChange = (newSortBy) => {
        setSortBy(newSortBy);
    };

    const toggleViewMode = () => {
        setViewMode(prev => prev === 'grid' ? 'list' : 'grid');
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (searchTerm.trim().length >= 2 || searchFilters.languages.length > 0 || searchFilters.processStages.length > 0) {
                console.log('Search triggered by Enter key');
                e.preventDefault();
                performSearch(searchTerm);
            }
        } else if (e.key === 'Escape') {
            console.log('Search cleared by Escape key');
            e.preventDefault();
            clearSearch();
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        window.location.href = '/login';
    };

    
    

    const hasActiveFilters = searchFilters.languages.length > 0 || searchFilters.processStages.length > 0;

    const getSearchDescription = () => {
        const parts = [];

        if (searchTerm) {
            parts.push(`name containing "${searchTerm}"`);
        }

        if (searchFilters.languages.length > 0) {
            parts.push(`language${searchFilters.languages.length > 1 ? 's' : ''}: ${searchFilters.languages.join(', ')}`);
        }

        if (searchFilters.processStages.length > 0) {
            const stageLabels = searchFilters.processStages.map(stage =>
                availableProcessStages.find(s => s.value === stage)?.label || stage
            );
            parts.push(`stage${searchFilters.processStages.length > 1 ? 's' : ''}: ${stageLabels.join(', ')}`);
        }

        return parts.join(', ');
    };

    // Create sorted versions of the folders and files
    const sortedData = useMemo(() => {

        // Determine which data to sort (search results or regular folders/files)
        const foldersToSort = searchResults ? searchResults.folders : folders;
        const filesToSort = searchResults ? searchResults.files : files;

        // Clone arrays to avoid mutating props
        const sortedFolders = foldersToSort ? [...foldersToSort] : [];
        const sortedFiles = filesToSort ? [...filesToSort] : [];
        // Sort folders
        sortedFolders.sort((a, b) => {
            switch (sortBy) {
                case SORT_OPTIONS.NAME_ASC:
                    return a.name.localeCompare(b.name);
                case SORT_OPTIONS.NAME_DESC:
                    return b.name.localeCompare(a.name);
                case SORT_OPTIONS.DATE_OLDEST:
                    return new Date(a.updatedAt) - new Date(b.updatedAt);
                case SORT_OPTIONS.DATE_NEWEST:
                    return new Date(b.updatedAt) - new Date(a.updatedAt);
                default:
                    return 0;
            }
        });

        // Sort files
        sortedFiles.sort((a, b) => {
            switch (sortBy) {
                case SORT_OPTIONS.NAME_ASC:
                    return (a.videoName || '').localeCompare(b.videoName || '');
                case SORT_OPTIONS.NAME_DESC:
                    return (b.videoName || '').localeCompare(a.videoName || '');
                case SORT_OPTIONS.DATE_OLDEST:
                    return new Date(a.updatedAt) -
                        new Date(b.updatedAt);
                case SORT_OPTIONS.DATE_NEWEST:
                    return new Date(b.updatedAt) -
                        new Date(a.updatedAt);
                default:
                    return 0;
            }
        });
        return { folders: sortedFolders, files: sortedFiles };
    }, [folders, files, searchResults, sortBy]);

    useEffect(() => {
        localStorage.setItem('folderSortBy', sortBy);
    }, [sortBy]);


    const handleMenuItemClick = (action) => {
        setAnchorEl(null);
        if (typeof action === 'function') {
            action();
        }
    };

    return (
            <Box sx={{
                width: '100%',
                backgroundColor: darkMode ? 'background.default' : '#f5f5f5',
                minHeight: '100vh'
            }}>
                <Paper sx={{
                    borderRadius: 0,
                    padding: 0
                }}>
                    <HelpOverlay
                        title="List View Guide"
                        buttonId="list-view-help-button"
                        helpItems={userViewHelpItems ? userViewHelpItems : []}
                        showButton={true}
                    />
                    <PageHeader
                        darkMode={darkMode}
                        toggleDarkMode={toggleDarkMode}
                        handleLogout={handleLogout}
                        showLogout={true}
                        showDarkMode={true}
                        themeVariant={themeVariant} 
                    />

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        padding: 1,
                        mb: 3
                    }}>
                        <Box className="breadcrumbs" sx={{ display: 'flex', alignItems: 'center' }}>
                            {!searchResults ? (
                                <FolderBreadcrumbs
                                    currentPath={normalizedPath}
                                    onNavigate={onFolderClick}
                                />
                            ) : (
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                    Search Results: {getSearchDescription()}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                        flexWrap: 'wrap',
                        gap: 2
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flexGrow: 1,
                            mr: { xs: 0, sm: 2 },
                            mb: { xs: 2, sm: 0 },
                            width: { xs: '100%', sm: 'auto' }
                        }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%'
                            }}>
                                <Box sx={{ display: 'flex', width: '100%', gap: 1, alignItems: 'center' }}>
                                    <TextField
                                        className="search-field"
                                        size="small"
                                        placeholder={searchCurrentFolderOnly ? `Search in ${normalizedPath}...` : "Search all files..."}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyDown={handleSearchKeyDown}
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    {isSearching ? (
                                                        <CircularProgress size={20} />
                                                    ) : (
                                                        <SearchIcon fontSize="small" />
                                                    )}
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {isSearching ? (
                                                        <CircularProgress size={20} />
                                                    ) : (
                                                        <>
                                                            {(searchTerm || hasActiveFilters) && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={clearAllSearch}
                                                                    aria-label="clear search"
                                                                >
                                                                    <ClearIcon fontSize="small" />
                                                                </IconButton>
                                                            )}

                                                            {searchTerm.trim().length >= 2 && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => performSearch(searchTerm)}
                                                                    aria-label="search"
                                                                >
                                                                    <SearchIcon fontSize="small" color="primary" />
                                                                </IconButton>
                                                            )}

                                                            <Tooltip title={searchCurrentFolderOnly ? "Searching this folder only" : "Searching all folders"}>
                                                                <Box
                                                                    onClick={() => setSearchCurrentFolderOnly(!searchCurrentFolderOnly)}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        ml: 1,
                                                                        cursor: 'pointer',
                                                                        borderLeft: '1px solid',
                                                                        borderColor: 'divider',
                                                                        pl: 1,
                                                                        height: '24px',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ mr: 0.5 }}
                                                                    >
                                                                        {searchCurrentFolderOnly ? "This folder" : "All folders"}
                                                                    </Typography>

                                                                    <Box
                                                                        sx={{
                                                                            width: '36px',
                                                                            height: '18px',
                                                                            borderRadius: '9px',
                                                                            position: 'relative',
                                                                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            padding: '2px',
                                                                            transition: 'background-color 0.3s ease',
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                position: 'absolute',
                                                                                left: searchCurrentFolderOnly ? '2px' : 'calc(100% - 16px)',
                                                                                width: '14px',
                                                                                height: '14px',
                                                                                borderRadius: '50%',
                                                                                backgroundColor: searchCurrentFolderOnly ? 'primary.main' : 'text.secondary',
                                                                                transition: 'left 0.3s ease, background-color 0.3s ease',
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Tooltip title="Advanced Search">
                                        <span>
                                            <Button
                                                size="small"
                                                variant={advancedSearchOpen ? "contained" : "outlined"}
                                                color={hasActiveFilters ? "secondary" : "primary"}
                                                onClick={toggleAdvancedSearch}
                                                sx={{
                                                    minWidth: 'auto',
                                                    height: '40px',
                                                    width: '40px',
                                                    p: 0
                                                }}
                                            >
                                                <TuneIcon fontSize="small" />
                                                {hasActiveFilters && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '2px',
                                                            right: '2px',
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'error.main'
                                                        }}
                                                    />
                                                )}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>

                                <Collapse in={advancedSearchOpen} timeout="auto" unmountOnExit>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            mt: 1,
                                            p: 2,
                                            borderRadius: 1,
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(0, 0, 0, 0.02)'
                                        }}
                                    >
                                        <Typography variant="subtitle2" gutterBottom fontWeight="medium">
                                            Advanced Search Filters
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
                                            <FormControl sx={{ minWidth: 200, flex: '1 1 200px' }} size="small">
                                                <InputLabel>Languages</InputLabel>
                                                <Select
                                                    multiple
                                                    value={searchFilters.languages}
                                                    onChange={(e) => setSearchFilters({
                                                        ...searchFilters,
                                                        languages: e.target.value
                                                    })}
                                                    renderValue={(selected) => (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => (
                                                                <Chip
                                                                    key={value}
                                                                    label={value}
                                                                    size="small"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                />
                                                            ))}
                                                        </Box>
                                                    )}
                                                    label="Languages"
                                                >
                                                    {availableLanguages.map((language) => (
                                                        <MenuItem key={language} value={language}>
                                                            <Checkbox
                                                                checked={searchFilters.languages.indexOf(language) > -1}
                                                                size="small"
                                                            />
                                                            {language}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <FormControl sx={{ minWidth: 200, flex: '1 1 200px' }} size="small">
                                                <InputLabel>Completed Process Stage</InputLabel>
                                                <Select
                                                    multiple
                                                    value={searchFilters.processStages}
                                                    onChange={(e) => setSearchFilters({
                                                        ...searchFilters,
                                                        processStages: e.target.value
                                                    })}
                                                    renderValue={(selected) => (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => {
                                                                const stageLabel = availableProcessStages.find(s => s.value === value)?.label || value;
                                                                return (
                                                                    <Chip
                                                                        key={value}
                                                                        label={stageLabel}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: `${getProcessStageColor(value)}20`,
                                                                            color: getProcessStageColor(value),
                                                                            borderColor: getProcessStageColor(value)
                                                                        }}
                                                                        variant="outlined"
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                    label="Process Stage"
                                                >
                                                    {availableProcessStages.map((stage) => (
                                                        <MenuItem key={stage.value} value={stage.value}>
                                                            <Checkbox
                                                                checked={searchFilters.processStages.indexOf(stage.value) > -1}
                                                                size="small"
                                                            />
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1
                                                            }}>
                                                                <Chip
                                                                    label={stage.label}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: `${getProcessStageColor(stage.value)}20`,
                                                                        color: getProcessStageColor(stage.value),
                                                                        borderColor: getProcessStageColor(stage.value),
                                                                        minWidth: 100
                                                                    }}
                                                                    variant="outlined"
                                                                />
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                justifyContent: 'flex-end',
                                                flex: '1 1 100px'
                                            }}>
                                                <Button
                                                    size="small"
                                                    onClick={clearFilters}
                                                    disabled={!hasActiveFilters}
                                                    variant="text"
                                                >
                                                    Clear Filters
                                                </Button>

                                                {searchTerm || hasActiveFilters ? (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => performSearch(searchTerm)}
                                                        sx={{ ml: 1 }}
                                                    >
                                                        Search
                                                    </Button>
                                                ) : null}
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Collapse>
                            </Box>
                        </Box>

                        <Box 
                            
                            sx={{
                            display: 'flex',
                            gap: 1.5,
                            flexWrap: 'wrap',
                            justifyContent: { xs: 'center', sm: 'flex-end' },
                            width: { xs: '100%', sm: 'auto' }
                        }}>
                            <SortSelector
                                
                                value={sortBy}
                                onChange={handleSortChange}
                            />

                            <Tooltip title="Toggle view">
                                <IconButton className="view-toggle" onClick={toggleViewMode}>
                                    {viewMode === 'grid' ? <ViewListIcon /> : <GridViewIcon />}
                                </IconButton>
                            </Tooltip>

                            <ToggleButtonGroup
                                className="view-assignement-button"
                                value={viewMode}
                                exclusive
                                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                                size="small"
                            >
                                <ToggleButton value="normal" aria-label="Normal view">
                                    <Tooltip title="Normal View">
                                        <FolderIcon fontSize="small" sx={{ display: 'flex' }}/>
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="assigned" aria-label="Assigned to me">
                                    <Tooltip title="Assigned to Me">
                                        <AssignmentIcon fontSize="small" sx={{ display: 'flex' }}/>
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: -8,
                                                    right: -10,
                                                    backgroundColor: 'error.main',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: 16,
                                                    height: 16,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 'bold',
                                                    boxShadow: '0 0 0 2px #fff'
                                                }}
                                            >
                                                {openAssignments.length}
                                            </Box>
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>

                            <Tooltip title="Refresh folder">
                                <Button
                                    className="refresh-button"
                                    color="success"
                                    startIcon={<RefreshIcon />}
                                    onClick={handleRefresh}
                                    
                                >
                                    Refresh
                                </Button>
                            </Tooltip>

                            <Tooltip title="Create new folder">
                                <Button
                                    className="create-folder-button"
                                    color="primary"
                                    startIcon={<CreateNewFolderIcon />}
                                    onClick={onCreateFolder}
                                    
                                >
                                    New Folder
                                </Button>
                            </Tooltip>

                            <Tooltip title="Upload new video">
                                <Button
                                    className="upload-video-button"
                                    color="secondary"
                                    startIcon={<VideoCallIcon />}
                                    onClick={() => onCreateVideo(normalizedPath)}
                                >
                                    New Video
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>
                </Paper>

                {searchResults ? (
                    <Box sx={{
                        mb: 4,
                        px: 1
                    }}>
                        {searchResults.error ? (
                            <Typography variant="body2" color="error">
                                {searchResults.error}
                                <Button
                                    size="small"
                                    sx={{ ml: 2 }}
                                    onClick={clearSearch}
                                    startIcon={<ArrowBackIcon />}
                                >
                                    Back to current folder
                                </Button>
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Found {searchResults.folders.length} folders and {searchResults.files.length} files matching "{searchTerm}"
                                {searchCurrentFolderOnly ? (
                                    normalizedPath !== '/' ? <span> in {normalizedPath}</span> : <span> in root folder</span>
                                ) : (
                                    <span> across all folders</span>
                                )}
                                <Button
                                    size="small"
                                    sx={{ ml: 2 }}
                                    onClick={clearSearch}
                                    startIcon={<ArrowBackIcon />}
                                >
                                    Back to current folder
                                </Button>
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Box sx={{
                        padding:1,
                        mb: 4,
                        display: 'flex',
                        gap: 2,
                        px: 1
                    }}>
                        <Chip
                            icon={<FolderIcon fontSize="small" sx={{ 
                                color: theme => themeVariant === 'vibrant' ? 
                                    '#ffffff' : 
                                    undefined 
                            }} />}
                            label={`${sortedData.folders.length} folders`}
                            color="primary"
                            variant={themeVariant === 'vibrant' ? 'filled' : 'outlined'}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                backgroundColor: theme => themeVariant === 'vibrant' ? 
                                    (theme.palette.mode === 'dark' ? 
                                        alpha(theme.palette.primary.main, 0.25) : 
                                        alpha(theme.palette.primary.main, 0.1)) : 
                                    'transparent',
                                color: theme => themeVariant === 'vibrant' ? 
                                    (theme.palette.mode === 'dark' ? 
                                        '#ffffff' :
                                        '#ffffff' ):
                                    theme.palette.primary.dark,
                                borderColor: theme => themeVariant === 'vibrant' ? 
                                    theme.palette.primary.main : 
                                    undefined,
                                borderWidth: themeVariant === 'vibrant' ? 2 : 1,
                                '& .MuiChip-icon': {
                                    color: theme => themeVariant === 'vibrant' ? 
                                        (theme.palette.mode === 'dark' ? 
                                            '#ffffff' : 
                                            theme.palette.primary.dark) : 
                                        undefined
                                }
                            }}
                        />
                        <Chip
                            icon={<VideoFileIcon fontSize="small" sx={{ 
                                color: theme => themeVariant === 'vibrant' ? 
                                    '#ffffff' : 
                                    undefined 
                            }} />}
                            label={`${sortedData.files.length} files`}
                            color="secondary"
                            variant={themeVariant === 'vibrant' ? 'filled' : 'outlined'}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                backgroundColor: theme => themeVariant === 'vibrant' ? 
                                    (theme.palette.mode === 'dark' ? 
                                        alpha(theme.palette.secondary.main, 0.25) : 
                                        alpha(theme.palette.secondary.main, 0.1)) : 
                                    'transparent',
                                color: theme => themeVariant === 'vibrant' ? 
                                    (theme.palette.mode === 'dark' ? 
                                        '#ffffff' :
                                       '#ffffff' ):
                                    theme.palette.primary.dark,
                                borderColor: theme => themeVariant === 'vibrant' ? 
                                    theme.palette.secondary.main : 
                                    undefined,
                                borderWidth: themeVariant === 'vibrant' ? 2 : 1,
                                '& .MuiChip-icon': {
                                    color: theme => themeVariant === 'vibrant' ? 
                                        (theme.palette.mode === 'dark' ? 
                                            '#ffffff' : 
                                            theme.palette.secondary.dark) : 
                                        undefined
                                }
                            }}
                        />
                    </Box>
                )}

                <Box className="file-grid" sx={{
                    borderRadius: 1,
                    overflow: 'hidden',
                    p: { xs: 1, sm: 2 }
                }}>
                    {viewMode === 'assigned' ? (
                        loadingAssignments ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <CircularProgress />
                            </Box>
                        ) : assignedVideos.length === 0 ? (
                            <EmptyState
                                icon={<AssignmentIcon sx={{ fontSize: 60 }} />}
                                title="No Assignments"
                                message="You don't have any videos assigned to you."
                            />
                        ) : (
                            <>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                                    Videos Assigned to You
                                </Typography>
                                <Grid container spacing={2}>
                                    {assignedVideos.map((video) => (
                                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={video.id}>
                                            <FileGridItem
                                                file={video}
                                                onFileClick={onFileClick}
                                                onMenuOpen={onMenuOpen}
                                                showPath={true}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </>
                        )
                    ) : viewMode === 'grid' ? (
                        <GridView
                            folders={sortedData.folders}
                            files={sortedData.files}
                            onFolderClick={onFolderClick}
                            onFileClick={onFileClick}
                            onMenuOpen={onMenuOpen}
                            showPaths={!!searchResults}
                        />
                    ) : (
                        <ListView
                            folders={sortedData.folders}
                            files={sortedData.files}
                            onFolderClick={onFolderClick}
                            onFileClick={onFileClick}
                            onMenuOpen={onMenuOpen}
                            showPaths={!!searchResults}
                        />
                    )}
                </Box>
            </Box>
    );
};

export default ModernFolderView;